import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildClassSession } from '@/state/classSession';
import { emptyLyrics, safeLyrics } from '@/lyrics/schema';
import {
  clearSeatBackup, purgeSeatBackups, readSeatBackup, seatSnapshot, showcaseNoticeKey,
  writeSeatBackup,
} from '@/state/seatSession';
import {
  createSeatBeat, deleteSeatBeat, getSeatTakeHome, loadSeatBeat, loadSeatSession,
  reserveSeatTakeHome, saveSeatBeat, reportSeatSaveStatus, SEAT_TOKEN_KEY,
  signOutSeat, updateSeatBeat, uploadSeatTakeHome,
} from '@/api/seat';
import { useNoReferrer } from './useNoReferrer';

const AI_DISABLED_CAPTION = "Pizzul's AI is off in class. Rhyme Brain still works.";
const sameSnapshot = (left, right) => JSON.stringify(left) === JSON.stringify(right);

function emptyTrainingBeat(pack) {
  const arrangement = buildClassSession(pack);
  return seatSnapshot(arrangement, emptyLyrics(), arrangement.bpm, arrangement.totalBars);
}

function emptyFreeBeat() {
  return { arrangement: { tracks: [], sectionMarkers: [], bpm: 90, totalBars: 32 }, lyrics: emptyLyrics() };
}

function packForSlot(session, slot) {
  return slot === 2 ? (session.pack2 || session.pack) : session.pack;
}

function initialStoredBeat(session, metadata) {
  if (!metadata?.arrangement) return null;
  const stored = {
    snapshot: { arrangement: metadata.arrangement, lyrics: safeLyrics(metadata.lyrics) },
    revision: metadata.revision || 0, updatedAt: metadata.updated_at || null,
  };
  const backup = readSeatBackup(sessionStorage, session.seat_id, session.token_version, metadata.slot);
  return { ...stored, snapshot: backup || stored.snapshot, dirty: Boolean(backup), loaded: true };
}

function beatProgress(beat) {
  const summary = beat?.summary || {};
  if (!Object.keys(summary).length) return 'Not started yet';
  if ((summary.reached || []).includes('outro')) return 'Done';
  const total = summary.flow === 2 ? 8 : 6;
  const step = Math.min(total, Math.max(1, Number(summary.furthest_step || 0) + 1));
  return `Step ${step} of ${total}`;
}

// A beat can go home once a training beat reached its Outro (or the teacher opened Showcase
// time), or a free beat has at least one sound. Otherwise the button would only show an error.
export function canTakeHome(beat, takehomeOpen = false) {
  const summary = beat?.summary || {};
  if (beat?.kind === 'free') return Number(summary.clips || 0) > 0;
  return Boolean(takehomeOpen) || (summary.reached || []).includes('outro') || Number(summary.furthest_step || 0) >= 5;
}

function editedLabel(value) {
  if (!value) return 'Not started yet';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Edited recently' : `Last edited ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

export function SeatSessionPage({ initialSession, token, onExit, renderEditor }) {
  useNoReferrer();
  const startsInLibrary = initialSession.free_beats_unlocked || initialSession.beats.some((beat) => beat.slot >= 3);
  const [session, setSession] = useState(initialSession);
  const sessionRef = useRef(initialSession);
  const [slot, setSlot] = useState(null);
  const [generation, setGeneration] = useState(0);
  const [saveStatus, setSaveStatus] = useState('saved');
  const [notice, setNotice] = useState('');
  const [switching, setSwitching] = useState(false);
  const [loadingBeat, setLoadingBeat] = useState(!startsInLibrary);
  const [ended, setEnded] = useState(false);
  const [renameSlot, setRenameSlot] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [openTakeHome, setOpenTakeHome] = useState(false);
  const timers = useRef({ debounce: null, retry: null, saveReport: null });
  const saving = useRef({});
  const saveFailing = useRef(false);
  const lastFailingReport = useRef(0);
  const retryDelay = useRef({});
  const mounted = useRef(true);
  const saveSlotRef = useRef(null);
  const currentSlot = useRef(null);
  const beats = useRef(null);
  if (beats.current === null) {
    beats.current = {};
    initialSession.beats.forEach((beat) => {
      const stored = initialStoredBeat(initialSession, beat);
      if (stored) beats.current[beat.slot] = stored;
    });
  }

  const replaceSession = useCallback((next) => {
    sessionRef.current = next;
    setSession(next);
  }, []);

  const terminalError = useCallback((error) => {
    if (error?.code !== 'seat_signed_out' && error?.code !== 'program_ended' && ![401, 410].includes(error?.status)) return false;
    clearTimeout(timers.current.debounce); clearTimeout(timers.current.retry);
    purgeSeatBackups();
    if (error.code === 'program_ended' || error.status === 410) {
      setEnded(true); onExit('');
    } else {
      sessionStorage.removeItem(SEAT_TOKEN_KEY); onExit('Your teacher reset your seat. Sign in again.');
    }
    return true;
  }, [onExit]);

  const applyControls = useCallback((controls) => {
    if (!controls || !mounted.current) return;
    const current = sessionRef.current;
    const next = {
      ...current,
      ...(Object.prototype.hasOwnProperty.call(controls, 'beats') ? controls : {}),
      program: {
        ...current.program,
        ...(controls.program || {}),
        focus: Boolean(controls.program?.focus ?? controls.focus ?? current.program.focus),
        unlocked_all: Boolean(controls.program?.unlocked_all ?? controls.unlocked_all ?? current.program.unlocked_all),
        lyrics_enabled: Boolean(controls.program?.lyrics_enabled ?? controls.lyrics_enabled ?? current.program.lyrics_enabled),
        takehome_open: Boolean(controls.program?.takehome_open ?? controls.takehome_open ?? current.program.takehome_open),
      },
    };
    if (Object.prototype.hasOwnProperty.call(controls, 'free_beats_unlocked')) next.free_beats_unlocked = Boolean(controls.free_beats_unlocked);
    if (Object.prototype.hasOwnProperty.call(controls, 'free_locked_reason')) next.free_locked_reason = controls.free_locked_reason;
    if (Object.prototype.hasOwnProperty.call(controls, 'recording_enabled')) next.recording_enabled = Boolean(controls.recording_enabled);
    replaceSession(next);
  }, [replaceSession]);

  const reportSaveFailure = useCallback((failing) => {
    if (saveFailing.current === failing) return;
    saveFailing.current = failing;
    clearTimeout(timers.current.saveReport);
    const elapsed = Date.now() - lastFailingReport.current;
    const delay = failing ? Math.max(500, 30000 - elapsed) : 0;
    timers.current.saveReport = setTimeout(() => {
      if (!mounted.current || saveFailing.current !== failing) return;
      const payload = failing
        ? { failing: true, known_revision: Math.max(0, ...Object.values(beats.current).map((beat) => beat.revision || 0)) }
        : { failing: false };
      if (failing) lastFailingReport.current = Date.now();
      reportSeatSaveStatus(token, payload).catch(() => {});
    }, delay);
  }, [token]);

  const fetchBeat = useCallback(async (number, sourceSession = sessionRef.current, force = false) => {
    if (!force && beats.current[number]?.loaded) return beats.current[number];
    const metadata = sourceSession.beats.find((beat) => beat.slot === number);
    let detail;
    try {
      detail = await loadSeatBeat(token, number);
    } catch (error) {
      if (error?.status !== 404 || number >= 3) throw error;
      detail = { ...emptyTrainingBeat(packForSlot(sourceSession, number)), revision: 0 };
    }
    const stored = {
      snapshot: { arrangement: detail.arrangement, lyrics: safeLyrics(detail.lyrics) },
      revision: detail.revision || 0,
      updatedAt: metadata?.updated_at || null,
      dirty: false,
      loaded: true,
    };
    const canRestoreBackup = number < 3 || sourceSession.free_beats_unlocked;
    const backup = force || !canRestoreBackup ? null : readSeatBackup(sessionStorage, sourceSession.seat_id, sourceSession.token_version, number);
    if (!canRestoreBackup) clearSeatBackup(sessionStorage, sourceSession.seat_id, sourceSession.token_version, number);
    if (backup) { stored.snapshot = backup; stored.dirty = true; }
    beats.current[number] = stored;
    retryDelay.current[number] = retryDelay.current[number] || 2000;
    return stored;
  }, [token]);

  const reloadConflict = useCallback(async (number) => {
    try {
      const [latestSession] = await Promise.all([loadSeatSession(token), fetchBeat(number, sessionRef.current, true)]);
      if (!mounted.current) return;
      const latest = beats.current[number];
      latest.dirty = false;
      clearSeatBackup(sessionStorage, latestSession.seat_id, latestSession.token_version, number);
      replaceSession(latestSession);
      setNotice('Your beat was updated on another computer. Loaded the latest.');
      setSaveStatus('saved');
      if (currentSlot.current === number) setGeneration((value) => value + 1);
    } catch (error) { terminalError(error); }
  }, [fetchBeat, replaceSession, terminalError, token]);

  const saveSlot = useCallback(async (number) => {
    const beat = beats.current[number];
    if (ended || !beat?.dirty) return true;
    if (number >= 3 && !sessionRef.current.free_beats_unlocked) {
      beat.dirty = false;
      clearSeatBackup(sessionStorage, sessionRef.current.seat_id, sessionRef.current.token_version, number);
      return true;
    }
    if (saving.current[number]) return saving.current[number];
    clearTimeout(timers.current.debounce); clearTimeout(timers.current.retry);
    const sent = beat.snapshot;
    if (currentSlot.current === number) setSaveStatus('saving');
    const operation = saveSeatBeat(token, number, {
      revision: beat.revision, arrangement: sent.arrangement, lyrics: sent.lyrics,
    }).then((result) => {
      if (!mounted.current) return true;
      beat.revision = result.revision; beat.updatedAt = result.updated_at;
      retryDelay.current[number] = 2000;
      if (sameSnapshot(beat.snapshot, sent)) {
        beat.dirty = false;
        clearSeatBackup(sessionStorage, sessionRef.current.seat_id, sessionRef.current.token_version, number);
      } else {
        writeSeatBackup(sessionStorage, sessionRef.current.seat_id, sessionRef.current.token_version, number, beat.snapshot);
        timers.current.debounce = setTimeout(() => saveSlotRef.current(number), 2000);
      }
      const current = sessionRef.current;
      replaceSession({ ...current, beats: current.beats.map((item) => item.slot === number ? { ...item, revision: result.revision, updated_at: result.updated_at } : item) });
      applyControls(result); reportSaveFailure(false);
      if (number === 2) loadSeatSession(token).then(applyControls).catch((error) => { terminalError(error); });
      if (currentSlot.current === number) setSaveStatus('saved');
      return true;
    }).catch(async (error) => {
      if (!mounted.current) return false;
      if (terminalError(error)) return false;
      if (error.code === 'revision_conflict' || (error.status === 409 && 'revision' in error)) {
        await reloadConflict(number); return false;
      }
      if (error.status === 403 && number >= 3) {
        try {
          const latestSession = await loadSeatSession(token);
          await fetchBeat(number, latestSession, true);
          beats.current[number].dirty = false;
          clearSeatBackup(sessionStorage, latestSession.seat_id, latestSession.token_version, number);
          applyControls(latestSession);
          if (currentSlot.current === number) setGeneration((value) => value + 1);
        } catch (_) { /* The next poll retries state. */ }
        setNotice('Your teacher paused free beats. You can still play and take it home.');
        setSaveStatus('saved');
        return false;
      }
      reportSaveFailure(true); beat.dirty = true;
      writeSeatBackup(sessionStorage, sessionRef.current.seat_id, sessionRef.current.token_version, number, beat.snapshot);
      if (currentSlot.current === number) setSaveStatus('retrying');
      const delay = retryDelay.current[number] || 2000;
      retryDelay.current[number] = Math.min(delay * 2, 30000);
      timers.current.retry = setTimeout(() => saveSlotRef.current(number), delay);
      return false;
    }).finally(() => { delete saving.current[number]; });
    saving.current[number] = operation;
    return operation;
  }, [applyControls, ended, fetchBeat, reloadConflict, replaceSession, reportSaveFailure, terminalError, token]);

  const openBeat = useCallback(async (number, takeHome = false) => {
    if (switching) return;
    setSwitching(true);
    const previous = currentSlot.current;
    const saved = previous == null || previous === number || await saveSlot(previous);
    if (saved) {
      try {
        setLoadingBeat(true);
        await fetchBeat(number);
        if (mounted.current) {
          currentSlot.current = number; setSlot(number); setOpenTakeHome(takeHome);
          setSaveStatus(beats.current[number].dirty ? 'saving' : 'saved');
          setGeneration((value) => value + 1);
          if (beats.current[number].dirty) timers.current.debounce = setTimeout(() => saveSlotRef.current(number), 2000);
        }
      } catch (error) {
        if (!terminalError(error)) setNotice(error.message || "Couldn't open this beat.");
      }
    }
    if (mounted.current) { setLoadingBeat(false); setSwitching(false); }
  }, [fetchBeat, saveSlot, switching, terminalError]);

  const changeBeat = useCallback((snapshot) => {
    const number = currentSlot.current;
    if (ended || number == null || (number >= 3 && !sessionRef.current.free_beats_unlocked)) return;
    const beat = beats.current[number];
    if (!beat || sameSnapshot(beat.snapshot, snapshot)) return;
    beat.snapshot = snapshot; beat.dirty = true;
    writeSeatBackup(sessionStorage, sessionRef.current.seat_id, sessionRef.current.token_version, number, snapshot);
    setSaveStatus('saving'); clearTimeout(timers.current.debounce);
    timers.current.debounce = setTimeout(() => saveSlotRef.current(number), 2000);
  }, [ended]);

  const editorReady = useCallback((snapshot) => {
    const number = currentSlot.current;
    if (number != null && beats.current[number]) beats.current[number].snapshot = snapshot;
  }, []);

  const poll = useCallback(async () => {
    if (ended || document.visibilityState !== 'visible') return;
    try {
      const controls = await loadSeatSession(token);
      const wasEditable = sessionRef.current.free_beats_unlocked;
      applyControls(controls);
      if (currentSlot.current >= 3 && wasEditable && controls.free_beats_unlocked === false) {
        const number = currentSlot.current;
        await fetchBeat(number, controls, true);
        beats.current[number].dirty = false;
        clearSeatBackup(sessionStorage, controls.seat_id, controls.token_version, number);
        setNotice('Your teacher paused free beats. You can still play and take it home.');
        setSaveStatus('saved'); setGeneration((value) => value + 1);
      }
    } catch (error) { terminalError(error); }
  }, [applyControls, ended, fetchBeat, terminalError, token]);

  useEffect(() => {
    const interval = setInterval(poll, 20000);
    const visibility = () => {
      if (document.visibilityState === 'hidden' && currentSlot.current != null) saveSlot(currentSlot.current);
      else poll();
    };
    document.addEventListener('visibilitychange', visibility);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', visibility); };
  }, [poll, saveSlot]);

  useEffect(() => {
    if (!session.program.takehome_open) return;
    const key = showcaseNoticeKey(session.seat_id, session.token_version);
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1'); setNotice('Showcase time! Tap Take it home to save your beat.');
  }, [session.program.takehome_open, session.seat_id, session.token_version]);

  saveSlotRef.current = saveSlot;
  useEffect(() => {
    mounted.current = true;
    if (!startsInLibrary) openBeat(1);
    const pending = timers.current;
    return () => {
      mounted.current = false;
      clearTimeout(pending.debounce); clearTimeout(pending.retry); clearTimeout(pending.saveReport);
    };
    // Initial route choice must not change when the teacher toggles free beats later.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = async () => {
    if (switching) return;
    setSwitching(true);
    const saved = currentSlot.current == null || await saveSlot(currentSlot.current);
    if (!saved) { if (mounted.current) setSwitching(false); return; }
    try { await signOutSeat(token); } catch (error) { if (terminalError(error)) return; }
    sessionStorage.removeItem(SEAT_TOKEN_KEY); purgeSeatBackups(); onExit('');
  };

  const backToLibrary = async () => {
    if (switching || currentSlot.current == null) return;
    setSwitching(true);
    const saved = await saveSlot(currentSlot.current);
    if (saved) {
      try { replaceSession(await loadSeatSession(token)); } catch (error) { terminalError(error); }
      currentSlot.current = null; setSlot(null); setOpenTakeHome(false); setNotice('');
    }
    if (mounted.current) setSwitching(false);
  };

  const lastTakeHomeRevision = useRef({});
  const createTakeHome = useCallback(async (renderMp3) => {
    const number = currentSlot.current;
    do {
      const saved = await saveSlot(number);
      if (!saved) throw Object.assign(new Error('save_failed'), { code: 'save_failed' });
    } while (beats.current[number].dirty);
    const revision = beats.current[number].revision;
    if (lastTakeHomeRevision.current[number] === revision) {
      try { return await getSeatTakeHome(token, number); } catch (error) { if (error?.status !== 404) throw error; }
    }
    const reservation = await reserveSeatTakeHome(token, number);
    const mp3 = await renderMp3();
    if (!mp3 || mp3.size === 0) throw Object.assign(new Error('render_failed'), { code: 'render_failed' });
    const result = await uploadSeatTakeHome(token, number, { mp3, revision, reservationId: reservation.reservation_id });
    lastTakeHomeRevision.current[number] = result?.beat_revision ?? revision;
    const current = sessionRef.current;
    replaceSession({ ...current, beats: current.beats.map((item) => item.slot === number ? { ...item, has_takehome: true } : item) });
    return result;
  }, [replaceSession, saveSlot, token]);

  const downloadBeat = useCallback(async (renderMp3) => {
    const mp3 = await renderMp3();
    if (!mp3 || mp3.size === 0) throw Object.assign(new Error('render_failed'), { code: 'render_failed' });
    const downloadUrl = URL.createObjectURL(mp3); const anchor = document.createElement('a');
    anchor.href = downloadUrl; anchor.download = `beat-${currentSlot.current}.mp3`;
    document.body.appendChild(anchor); anchor.click(); document.body.removeChild(anchor); URL.revokeObjectURL(downloadUrl);
  }, []);

  const createNewBeat = async () => {
    if (!session.free_beats_unlocked || switching) return;
    setSwitching(true); setNotice('');
    try {
      const created = await createSeatBeat(token);
      const latest = await loadSeatSession(token); replaceSession(latest);
      setSwitching(false); await openBeat(created.slot);
    } catch (error) {
      setNotice(error.message || "Couldn't make a new beat."); setSwitching(false);
    }
  };

  const patchMetadata = async (number, value) => {
    try {
      const result = await updateSeatBeat(token, number, value);
      const current = sessionRef.current;
      replaceSession({ ...current, beats: current.beats.map((item) => ({
        ...item,
        ...(item.slot === number ? result : {}),
        ...(result.starred === true && item.slot !== number ? { starred: false } : {}),
      })) });
      return true;
    } catch (error) { setNotice(error.message || "Couldn't update this beat."); return false; }
  };

  const removeBeat = async (number) => {
    if (!window.confirm("Delete this beat? This can't be undone.")) return;
    try {
      await deleteSeatBeat(token, number);
      clearSeatBackup(sessionStorage, session.seat_id, session.token_version, number);
      delete beats.current[number];
      const current = sessionRef.current;
      replaceSession({ ...current, beats: current.beats.filter((item) => item.slot !== number) });
    } catch (error) { setNotice(error.message || "Couldn't delete this beat."); }
  };

  const libraryBeats = useMemo(() => {
    const bySlot = new Map(session.beats.map((beat) => [beat.slot, beat]));
    [1, 2].forEach((number) => {
      if (!bySlot.has(number)) bySlot.set(number, { slot: number, kind: 'training', title: `Beat ${number}`, summary: {}, revision: 0, has_takehome: false });
    });
    return [...bySlot.values()].sort((left, right) => left.slot - right.slot);
  }, [session.beats]);
  const freeCount = libraryBeats.filter((beat) => beat.slot >= 3).length;
  const capReached = freeCount >= Number(session.free_beat_limit || 30);
  const newBeatReason = capReached ? "You've made 30 beats. Delete one to make room."
    : !session.free_beats_unlocked
      ? session.free_locked_reason === 'teacher_paused' ? 'Your teacher paused free beats' : 'Finish Beat 2 to unlock free beats'
      : '';

  const activeMetadata = slot == null ? null : libraryBeats.find((beat) => beat.slot === slot);
  const activeFree = Boolean(activeMetadata?.kind === 'free' || slot >= 3);
  const readOnly = activeFree && !session.free_beats_unlocked;
  const editorProps = useMemo(() => slot == null || !beats.current[slot] ? null : ({
    classPack: packForSlot(session, slot),
    seatMode: {
      beat: beats.current[slot].snapshot,
      title: activeMetadata?.title || `Beat ${slot}`,
      free: activeFree,
      readOnly,
      recordingEnabled: Boolean(session.recording_enabled),
      focus: activeFree ? false : session.program.focus && !session.program.unlocked_all,
      unlockedAll: activeFree || session.program.unlocked_all,
      lyricsEnabled: activeFree || session.program.lyrics_enabled,
      takehomeOpen: activeFree || session.program.takehome_open,
      classCode: session.program.class_code,
      aiDisabledCaption: AI_DISABLED_CAPTION,
      onChange: changeBeat, onReady: editorReady, createTakeHome, downloadBeat,
      showTakeHome: () => getSeatTakeHome(token, currentSlot.current),
      slot, openTakeHome,
    },
    // generation deliberately remounts the editor after detail/conflict loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [activeFree, activeMetadata?.title, changeBeat, createTakeHome, downloadBeat, editorReady, generation, openTakeHome, readOnly, session, slot, token]);

  if (ended) return <main className="join-page" data-skin="playground" role="status"><div className="join-card join-ended"><h1>Opening your beats...</h1></div></main>;

  return <div className="seat-session-page" data-skin="playground">
    <header className="seat-session-header">
      <strong>Seat {session.seat_number} <span aria-hidden="true">·</span> {session.program.name}</strong>
      {slot == null ? <span className="seat-library-heading">Your beats</span> : <>
        {startsInLibrary || session.free_beats_unlocked || session.beats.some((beat) => beat.slot >= 3)
          ? <button className="seat-back-library" disabled={switching} onClick={backToLibrary}>Back to Your beats</button>
          : <div className="seat-beat-switcher" role="group" aria-label="Choose a beat">{[1, 2].map((number) => <button key={number} aria-pressed={slot === number} disabled={switching} onClick={() => openBeat(number)}>Beat {number}</button>)}</div>}
        <span className={`seat-save-status ${saveStatus || ''}`} role="status">{saveStatus === 'saving' ? 'Saving...' : saveStatus === 'retrying' ? "Couldn't save, trying again" : saveStatus === 'saved' ? 'Saved' : ''}</span>
      </>}
      <button className="seat-signout" disabled={switching} onClick={signOut}>Sign out of my seat</button>
    </header>
    {notice && <div className="seat-session-notice" role="status"><img src="/Pizzul_Hero.png" alt="" />{notice}</div>}
    {slot == null ? <main className="seat-library" data-testid="your-beats">
      <div className="seat-library-intro"><div><p>Your seat saves every beat automatically.</p><h1>Your beats</h1></div><button className="seat-new-beat" disabled={Boolean(newBeatReason) || switching} onClick={createNewBeat}>New beat</button></div>
      {newBeatReason && <p className="seat-new-reason" role="status">{newBeatReason}</p>}
      <section className="seat-beat-cards" aria-label="Your beats">
        {libraryBeats.map((beat) => <article className={`seat-beat-card ${beat.kind}`} key={beat.slot} data-testid={`beat-card-${beat.slot}`}>
          <div className="seat-card-heading"><span>{beat.kind === 'training' ? 'Training beat' : 'Free beat'}</span>{beat.kind === 'free' && <button className="seat-star" aria-label={`${beat.starred ? 'Unstar' : 'Star'} ${beat.title}`} aria-pressed={Boolean(beat.starred)} onClick={() => patchMetadata(beat.slot, { starred: !beat.starred })}>{beat.starred ? '★' : '☆'}</button>}</div>
          {renameSlot === beat.slot ? <form className="seat-rename" onSubmit={async (event) => { event.preventDefault(); if (await patchMetadata(beat.slot, { title: renameValue })) setRenameSlot(null); }}><label><span className="sr-only">Beat name</span><input autoFocus maxLength={40} value={renameValue} onChange={(event) => setRenameValue(event.target.value)} /></label><button type="submit">Save name</button><button type="button" onClick={() => setRenameSlot(null)}>Cancel</button></form> : <h2>{beat.title || `Beat ${beat.slot}`}</h2>}
          <p>{beat.kind === 'training' ? beatProgress(beat) : editedLabel(beat.updated_at)}</p>
          {!beat.has_takehome && canTakeHome(beat, session.program?.takehome_open) && <small>Take it home before your class ends</small>}
          <div className="seat-card-actions">
            <button className="primary" disabled={switching} onClick={() => openBeat(beat.slot)}>Open</button>
            {(beat.has_takehome || canTakeHome(beat, session.program?.takehome_open)) && <button disabled={switching} onClick={() => openBeat(beat.slot, true)}>Take it home</button>}
            {beat.kind === 'free' && <><button disabled={!session.free_beats_unlocked || switching} onClick={() => { setRenameSlot(beat.slot); setRenameValue(beat.title || ''); }}>Rename</button><button className="danger" disabled={!session.free_beats_unlocked || switching} onClick={() => removeBeat(beat.slot)}>Delete</button></>}
          </div>
        </article>)}
      </section>
    </main> : loadingBeat || !editorProps ? <main className="seat-beat-loading" role="status">Loading your beat...</main> : <>
      {readOnly && <div className="seat-readonly-banner" role="status">Your teacher paused free beats. You can still play and take it home.</div>}
      <div className="seat-editor">{renderEditor({ ...editorProps, key: `${slot}:${generation}` })}</div>
    </>}
  </div>;
}

export default SeatSessionPage;
